import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BookingProvider } from './context/BookingContext';

const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const CampCalendar = lazy(() => import('./pages/CampCalendar'));
const BookingForm = lazy(() => import('./pages/BookingForm'));
const Payment = lazy(() => import('./pages/Payment'));
const Confirmation = lazy(() => import('./pages/Confirmation'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const RewardsStore = lazy(() => import('./pages/RewardsStore'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));

// New pages
const Discover = lazy(() => import('./pages/Discover'));
const OurStory = lazy(() => import('./pages/OurStory'));
const CampsEvents = lazy(() => import('./pages/CampsEvents'));
const WellnessRetreat = lazy(() => import('./pages/WellnessRetreat'));
const Moments = lazy(() => import('./pages/Moments'));
const AffiliatePartner = lazy(() => import('./pages/AffiliatePartner'));
const Volunteer = lazy(() => import('./pages/Volunteer'));
const AmbassadorPartner = lazy(() => import('./pages/AmbassadorPartner'));
const EventPartner = lazy(() => import('./pages/EventPartner'));
const Connect = lazy(() => import('./pages/Connect'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const GrandPrix = lazy(() => import('./pages/GrandPrix'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const AffiliateDashboard = lazy(() => import('./pages/AffiliateDashboard'));
const GlampingPartner = lazy(() => import('./pages/GlampingPartner'));
const CampSchedule = lazy(() => import('./pages/CampSchedule'));

const LoadingScreen = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f1f0f', flexDirection: 'column', gap: 24 }}>
    <img
      src="/images/discovery-park-logo.png"
      alt="The Discovery Park"
      className="loading-logo-pulse"
      style={{ width: 120, height: 120, objectFit: 'contain' }}
    />
    <p style={{ color: '#ffa239', fontWeight: 700, letterSpacing: 3, fontSize: 13 }}>LOADING...</p>
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return user ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const AffiliateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isAffiliate) return <Navigate to="/dashboard" replace />;
  return children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const PageTransition = ({ children }) => {
  const location = useLocation();
  return <div className="page-fade-in" key={location.pathname}>{children}</div>;
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <BookingProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              style: { background: '#1a2e1a', color: '#e8f0e0', border: '1px solid #3a5a3a' },
              success: { iconTheme: { primary: '#ffa239', secondary: '#0f1f0f' } },
            }}
          />
          <Suspense fallback={<LoadingScreen />}>
            <PageTransition>
            <Routes>
              {/* Public pages */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/calendar" element={<CampCalendar />} />

              {/* New public pages */}
              <Route path="/discover" element={<Discover />} />
              <Route path="/our-story" element={<OurStory />} />
              <Route path="/camps-events" element={<CampsEvents />} />
              <Route path="/grand-prix" element={<GrandPrix />} />
              <Route path="/wellness" element={<WellnessRetreat />} />
              <Route path="/moments" element={<Moments />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/partner/affiliate" element={<AffiliatePartner />} />
              <Route path="/partner/volunteer" element={<Volunteer />} />
              <Route path="/partner/ambassador" element={<AmbassadorPartner />} />
              <Route path="/partner/event-partner" element={<EventPartner />} />
              <Route path="/connect" element={<Connect />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/glamping" element={<GlampingPartner />} />
              <Route path="/camp-schedule" element={<CampSchedule />} />

              {/* Protected pages */}
              <Route path="/book" element={<PrivateRoute><BookingForm /></PrivateRoute>} />
              <Route path="/payment" element={<PrivateRoute><Payment /></PrivateRoute>} />
              <Route path="/confirmation" element={<PrivateRoute><Confirmation /></PrivateRoute>} />
              <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/rewards" element={<RewardsStore />} />

              {/* Affiliate */}
              <Route path="/affiliate-dashboard" element={<AffiliateRoute><AffiliateDashboard /></AffiliateRoute>} />

              {/* Admin */}
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </PageTransition>
          </Suspense>
        </BookingProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
